<script setup>
import { ref, computed, watch, reactive, onMounted, onUnmounted, nextTick } from 'vue'
import { NOTE_MAX_LENGTH } from '@/utils/profileNotes'
import { mediaUrl } from '@core/utils/mediaUrl'
import { orderCities } from '@/utils/cityOrder'
import { storeToRefs } from 'pinia'
import { useDisplay } from 'vuetify'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import moment from 'moment-timezone'
import {
  useUsersStore,
  useRestaurantsStore,
  useReservationsStore,
  useUserGroupsStore,
  usePreferredStatusStore,
} from "@/stores"
import { dedupeById } from '@/stores/restaurants.store'
import { useInstantBookingSlotsStore } from '@/stores/instantBookingSlots.store'
import RestaurantGridCard from "@core/components/home/content/RestaurantGridCard.vue"
import CmsText from '@/@core/components/CmsText.vue'
import PreferredIcon from '@core/components/PreferredIcon.vue'
import RestaurantHeart from '@core/components/RestaurantHeart.vue'
import DatePickerDialog from '@core/components/DatePickerDialog.vue'
import AddRestaurantDialog from '@core/components/AddRestaurantDialog.vue'
import InstantBookingCheckoutDialog from '@core/components/InstantBookingCheckoutDialog.vue'
import ExternalBookingDialog from '@core/components/ExternalBookingDialog.vue'
import UpgradeProfileDialog from '@core/components/UpgradeProfileDialog.vue'
import { getRestaurantImageUrl as getRestaurantImageUrlShared } from '@core/utils/restaurantImage'

const usersStore = useUsersStore()
const restaurantsStore = useRestaurantsStore()
const reservationsStore = useReservationsStore()
const userGroupsStore = useUserGroupsStore()
const instantBookingSlotsStore = useInstantBookingSlotsStore()
const preferredStatusStore = usePreferredStatusStore()
const { smAndDown } = useDisplay()
const route = useRoute()
const { t } = useI18n()

let myUser = null
let isShowingPreferredRequestInfoDialog = null
let isShowingRestaurantsInfoDialog = null
let loginDialogMode = null
let editingReservation = null;
({ myUser, isShowingPreferredRequestInfoDialog, isShowingRestaurantsInfoDialog, loginDialogMode } = storeToRefs(usersStore));
({ editingReservation } = storeToRefs(reservationsStore))

const { preferredRestaurants, bookedRestaurants, favoritedRestaurants, friendsFavoriteRestaurants, citySelectorData } = storeToRefs(restaurantsStore)
const { slots: instantBookingSlots, loading: instantBookingSlotsLoading } = storeToRefs(instantBookingSlotsStore)
const { viewingDetailsRestaurant, viewingDetailsRestaurantTab, favoriteNotes } = storeToRefs(restaurantsStore)

// Default city filter shown on every app open (in-memory only, no cross-session persistence)
const DEFAULT_MARKET = 'Chicago, IL'

const selectedCity = ref(DEFAULT_MARKET)
const bookingsSelectedCity = ref(DEFAULT_MARKET) // Separate state for bookings tab
const favoritesSelectedCity = ref(DEFAULT_MARKET) // Separate state for favorites tab
const instantBookingsSelectedCity = ref(DEFAULT_MARKET) // Separate state for instant bookings tab
const instantBookingsSelectedDate = ref(null) // Date filter for instant bookings
const isAddRestaurantDialogOpen = ref(false)
const showCheckoutDialog = ref(false)
const showExternalBookingDialog = ref(false)
const showUpgradeDialog = ref(false)
const upgradeProductId = ref(null)
const upgradePriceDollars = ref(null)
const upgradeMessage = ref(null)

const requestUpgrade = () => {
  upgradeProductId.value = 'individual-1cycle-annual'
  upgradePriceDollars.value = 300
  upgradeMessage.value = `purchased on ${moment().tz('America/Chicago').format('M/D/YY')}`
  showUpgradeDialog.value = true
}

const onUpgradeDialogComplete = async () => {
  showUpgradeDialog.value = false
  await usersStore.getMyUser()
}

const onUpgradeDialogCancel = () => {
  showUpgradeDialog.value = false
}
const selectedSlotId = ref(null)
const isLoading = ref(false)
const isLoadingMore = ref(false)
const hasMoreData = ref(true)
const isPrefetching = ref(false)
const hasPrefetchedNext = ref(false)
const loadMoreSentinelEl = ref(null)
let loadMoreObserver = null
let prefetchObserver = null
// Trending is the default for everyone — logged in or out
const favoritesFilterSelection = ref('Popular')
const showInstantBookingsTab = false // Hidden for now, may enable later
const bookingsOccasionFilter = ref('') // Occasion filter for bookings tab

// City filter bottom sheet state
const isCityFilterOpen = ref(false)
const citySearchQuery = ref('')
const pendingCitySelection = ref('')

// Occasion filter options for bookings tab
const occasionFilterOptions = [
  { text: 'All', value: '' },
  { text: 'Anniversary', value: 'Anniversary' },
  { text: 'Birthday', value: 'Birthday' },
  { text: 'Business', value: 'Business' },
  { text: 'Date', value: 'Date' },
  { text: 'General', value: 'General' },
  { text: 'Special Occasion', value: 'Special Occasion' }
]

// Computed sort value for favorites - 'alpha' for A-Z, 'popular' for Most Popular and Preferred
const favoritesSort = computed(() => {
  if (favoritesFilterSelection.value === 'Alpha') return 'alpha'
  if (favoritesFilterSelection.value === 'Popular') return 'popular'
  if (favoritesFilterSelection.value === 'Preferred') return 'popular' // Same sort as Most Popular, but filtered client-side
  
  return null
})

// Check if user is logged in (not a dummy/guest user)
const isUserLoggedIn = computed(() => {
  return myUser.value && myUser.value.userLevel !== 'dummy'
})

// Async fetch friends-favorited data for a list of restaurants (non-blocking)
const fetchFriendsDataForRestaurants = restaurants => {
  if (!isUserLoggedIn.value) return

  // Extract restaurant ID — some items have it at top level (_id), others nest it in .raw._id
  const ids = restaurants?.map(r => r.raw?._id || r.raw?.id || r._id || r.id).filter(Boolean)
  if (ids?.length) {
    restaurantsStore.fetchFriendsFavoritedBatch(ids)
  }
}

// Tab state - 'preferred', 'favorites', or 'instantBookings'
// Default to 'favorites' for all users
const activeTab = ref('favorites')

// Pagination state for each tab with prefetch cache
const pagination = reactive({
  bookings: {
    limit: 20,
    skip: 0,
    prefetchedData: null // Cache for prefetched data
  },
  preferred: {
    limit: 20,
    skip: 0,
    prefetchedData: null // Cache for prefetched data
  },
  favorites: {
    limit: 20,
    skip: 0,
    prefetchedData: null // Cache for prefetched data
  },
  friendsFavorites: {
    limit: 20,
    skip: 0,
    prefetchedData: null
  }
})

// City selector data specific to favorites tab (initialize with default "All Cities" option)
const favoritedCitySelectorData = ref({ cities: [{ text: 'All Cities', value: '' }] })

// City selector data specific to bookings tab (initialize with default "All Cities" option)
const bookedCitySelectorData = ref({ cities: [{ text: 'All Cities', value: '' }] })

// Helper to extract just the city name from "City, State" format
const getCityDisplayName = cityState => {
  if (!cityState) return cityState
  const parts = cityState.split(', ')

  return parts[0] || cityState
}

// Filter options - horizontal chips (Instagram/Google Maps style)
const favoritesFilterOptions = computed(() => {
  // Text-only tabs; order is Trending · Preferred · Just Booked · Network
  // for logged-in and logged-out alike
  const options = [
    { text: t('discover.filters.trending'), value: 'Popular' },
    { text: t('discover.filters.preferred'), value: 'Preferred' },
    { text: t('discover.filters.justBooked'), value: 'JustBooked' },
  ]

  options.push({ text: t('discover.filters.network'), value: 'Friends' })

  return options
})

// Logged-out prospects get a calm gated line on Just Booked and Network —
// no feed, no venue names, no login CTA (prospects can't sign in), and the
// Preferred Restaurants hint is hidden so the screen stays clean.
const isGatedLoggedOutTab = computed(() =>
  !isUserLoggedIn.value && ['JustBooked', 'Friends'].includes(favoritesFilterSelection.value))


// Simple city selector using backend data - varies by active tab
// With pagination, always use API data for accurate counts
const selectableCities = computed(() => {
  let cities = []

  if (activeTab.value === 'favorites') {
    // The "Just Booked" filter has its own server-side city list; the other
    // favorites filters share the favorited-city list.
    if (favoritesFilterSelection.value === 'JustBooked') {
      cities = bookedCitySelectorData.value?.cities || []
    } else {
      cities = favoritedCitySelectorData.value?.cities || []
    }
  } else {
    cities = citySelectorData.value?.cities || []
  }

  // Most-requested markets first, then All Cities, then the rest A–Z
  return orderCities(cities)
})

// Get filtered restaurants based on active tab and selected city
const displayedRestaurants = computed(() => {
  if (activeTab.value === 'preferred') {
    if (!preferredRestaurants.value) return []
    if (!selectedCity.value) return preferredRestaurants.value.filter(r => r.status === 'Preferred')
    
    return preferredRestaurants.value.filter(r => r.status === 'Preferred' && r.market === selectedCity.value)
  } else if (activeTab.value === 'instantBookings') {
    return [] // Instant bookings use slotsByRestaurant instead
  } else {
    // favorites tab - city filter is server-side, type filter is client-side

    // Handle "Just Booked" filter - show booked restaurants
    if (favoritesFilterSelection.value === 'JustBooked') {
      if (!bookedRestaurants.value) return []

      return bookedRestaurants.value
    }

    // Handle "Friends" filter - show friends' favorite restaurants
    if (favoritesFilterSelection.value === 'Friends') {
      if (!friendsFavoriteRestaurants.value) return []

      return friendsFavoriteRestaurants.value
    }

    // Handle "Preferred" filter — server-filtered and server-sorted by the
    // same trending score as Trending; rendered as-is (no client filtering)
    if (favoritesFilterSelection.value === 'Preferred') {
      return preferredRestaurants.value || []
    }

    // Handle "MyStatus" filter - show restaurants where the member has Preferred status
    if (favoritesFilterSelection.value === 'MyStatus') {
      if (!preferredRestaurants.value) return []
      const ids = preferredStatusStore.resolvedRestaurantIds
      let statusRestaurants = preferredRestaurants.value.filter(r =>
        ids.includes((r._id || r.id || '').toString())
      )
      if (selectedCity.value) {
        statusRestaurants = statusRestaurants.filter(r => r.market === selectedCity.value)
      }

      return statusRestaurants
    }

    if (!favoritedRestaurants.value) return []

    let filtered = favoritedRestaurants.value

    // Apply type filter (client-side only, city and A-Z sort are filtered server-side)
    if (favoritesFilterSelection.value !== 'All' && favoritesFilterSelection.value !== 'Alpha') {
      filtered = filtered.filter(r => {
        if (favoritesFilterSelection.value === 'Happenings') {
          return r.showHappeningsIcon === true
        } else if (favoritesFilterSelection.value === 'Spaces') {
          return r.eventSpaces && r.eventSpaces.length > 0
        }
        
        return true
      })
    }

    // Note: A-Z sorting is now handled on the backend via sort='alpha' parameter

    return filtered
  }
})

// Instant bookings: Get unique cities from slots
const availableInstantBookingCities = computed(() => {
  const cities = new Set()
  instantBookingSlots.value.forEach(slot => {
    if (slot.restaurantMarket) {
      cities.add(slot.restaurantMarket)
    }
  })
  
  return Array.from(cities).sort()
})

// Instant bookings: Get unique dates from slots
const availableInstantBookingDates = computed(() => {
  const dates = new Set()
  instantBookingSlots.value.forEach(slot => {
    if (slot.date) {
      const tz = slot.restaurantTimeZone || 'America/New_York'
      const localDate = moment.tz(slot.date, tz).format('YYYY-MM-DD')
      dates.add(localDate)
    }
  })
  
  return Array.from(dates).sort()
})

// Instant bookings: Filter slots
const filteredInstantBookingSlots = computed(() => {
  const now = new Date()

  // Filter by status and expiration time
  let filtered = instantBookingSlots.value.filter(slot => {
    const isAvailable = slot.status === 'available'
    const notExpired = slot.expirationTime ? new Date(slot.expirationTime) >= now : true
    
    return isAvailable && notExpired
  })

  if (instantBookingsSelectedCity.value) {
    filtered = filtered.filter(slot => slot.restaurantMarket === instantBookingsSelectedCity.value)
  }

  if (instantBookingsSelectedDate.value) {
    filtered = filtered.filter(slot => {
      const tz = slot.restaurantTimeZone || 'America/New_York'
      const slotDate = moment.tz(slot.date, tz).format('YYYY-MM-DD')
      
      return slotDate === instantBookingsSelectedDate.value
    })
  }

  return filtered
})

// Instant bookings: Group slots by restaurant
const slotsByRestaurant = computed(() => {
  const grouped = {}

  filteredInstantBookingSlots.value.forEach(slot => {
    const restaurantId = slot.restaurantId
    if (!grouped[restaurantId]) {
      grouped[restaurantId] = {
        restaurantId,
        restaurantName: slot.restaurantName,
        restaurantMarket: slot.restaurantMarket,
        restaurant: slot.restaurant,
        slots: []
      }
    }
    grouped[restaurantId].slots.push(slot)
  })

  // Sort slots within each restaurant by date and time
  Object.values(grouped).forEach(restaurant => {
    restaurant.slots.sort((a, b) => {
      const dateCompare = new Date(a.date) - new Date(b.date)
      if (dateCompare !== 0) return dateCompare
      
      return a.time.localeCompare(b.time)
    })
  })

  return Object.values(grouped).sort((a, b) =>
    a.restaurantName.localeCompare(b.restaurantName)
  )
})

// Check if current tab has data
const hasDataForActiveTab = computed(() => {
  if (activeTab.value === 'instantBookings') {
    return filteredInstantBookingSlots.value.length > 0
  }
  
  return displayedRestaurants.value.length > 0
})

// Dynamic city selector message based on active tab
const citySelectorMessage = computed(() => {
  // Removed per issue #6 - no longer showing "# of favorited restaurants" message
  return ''
})

// Watch for city changes and update tab-specific in-memory state (no cross-session persistence)
watch(selectedCity, newCity => {
  // Update tab-specific city state
  if (activeTab.value === 'favorites') {
    if (favoritesFilterSelection.value === 'JustBooked') {
      bookingsSelectedCity.value = newCity
    } else {
      favoritesSelectedCity.value = newCity
    }
  } else if (activeTab.value === 'instantBookings') {
    instantBookingsSelectedCity.value = newCity
  }
})

// Watch for filter changes to reload data from backend
watch(favoritesFilterSelection, async (newFilter, oldFilter) => {
  // Only reload if on favorites tab and filter actually changed
  if (activeTab.value === 'favorites' && newFilter !== oldFilter) {
    isLoading.value = true
    try {
      hasMoreData.value = true
      hasPrefetchedNext.value = false

      if (newFilter === 'JustBooked') {
        // Skip API call if user is not logged in or not platinum
        if (!isUserLoggedIn.value || !myUser.value?.isPlatinum) {
          isLoading.value = false

          return
        }

        // Clear stale data so loading spinner shows instead of old results
        restaurantsStore.bookedRestaurants = []

        // Load booked restaurants
        pagination.bookings.skip = 0
        pagination.bookings.prefetchedData = null
        await restaurantsStore.getBookedRestaurants(
          pagination.bookings.limit,
          false,
          selectedCity.value || null,
          0,
          false,
          false,
          bookingsOccasionFilter.value || null
        )

        // Preload booked city selector data
        if (!bookedCitySelectorData.value?.cities || bookedCitySelectorData.value.cities.length <= 1) {
          bookedCitySelectorData.value = await restaurantsStore.getBookedCitySelectorData()
        }
      } else if (newFilter === 'Friends') {
        // Skip API call if user is not logged in — Network tab prompts login instead
        if (!isUserLoggedIn.value) {
          restaurantsStore.friendsFavoriteRestaurants = []
          isLoading.value = false

          return
        }

        // Clear stale data so loading spinner shows instead of old results
        restaurantsStore.friendsFavoriteRestaurants = []
        pagination.friendsFavorites.skip = 0
        pagination.friendsFavorites.prefetchedData = null
        await restaurantsStore.getFriendsFavoriteRestaurants(
          pagination.friendsFavorites.limit,
          selectedCity.value || null,
          0
        )
      } else if (newFilter === 'Preferred') {
        // Same paginated flow as Trending — server-filtered to Preferred
        // and server-sorted by the shared trending score (sort=popular).
        // No client-side filtering, no oversized one-shot payloads.
        restaurantsStore.preferredRestaurants = []
        pagination.preferred.skip = 0
        pagination.preferred.prefetchedData = null
        await restaurantsStore.getPreferredRestaurants(
          pagination.preferred.limit,
          selectedCity.value || null,
          0,
          false,
          false,
          'popular'
        )
      } else if (newFilter === 'MyStatus') {
        await Promise.all([
          preferredStatusStore.loadPreferredStatus(),
          preferredRestaurants.value.length === 0
            ? restaurantsStore.getPreferredRestaurants(200, null, 0, false)
            : Promise.resolve(),
        ])
      } else {
        // Clear stale data so loading spinner shows instead of old results
        restaurantsStore.favoritedRestaurants = []

        // Load favorited restaurants with appropriate sort
        pagination.favorites.skip = 0
        pagination.favorites.prefetchedData = null

        // Use selectedCity.value (current visible selection) to honor user's city filter
        await restaurantsStore.getFavoritedRestaurants(
          pagination.favorites.limit,
          selectedCity.value || null,
          0,
          false,
          false,
          favoritesSort.value
        )
      }

      // Fetch friends-favorited data for the newly loaded restaurants
      if (newFilter !== 'Friends') {
        fetchFriendsDataForRestaurants(displayedRestaurants.value)
      }
    } finally {
      isLoading.value = false
    }
  }
})

// Watch for occasion filter changes to reload data from backend
watch(bookingsOccasionFilter, async (newOccasion, oldOccasion) => {
  // Only reload if using "Just Booked" filter and occasion actually changed
  if (activeTab.value === 'favorites' && favoritesFilterSelection.value === 'JustBooked' && newOccasion !== oldOccasion) {
    isLoading.value = true
    try {
      // Clear stale data and reset pagination
      restaurantsStore.bookedRestaurants = []
      pagination.bookings.skip = 0
      pagination.bookings.prefetchedData = null
      hasMoreData.value = true
      hasPrefetchedNext.value = false
      await restaurantsStore.getBookedRestaurants(
        pagination.bookings.limit,
        false,
        selectedCity.value || null,
        0,
        false,
        false,
        newOccasion || null
      )
    } finally {
      isLoading.value = false
    }
  }
})

// Note: Removed watcher that switched tabs on logout
// All users can now view both tabs

// Consolidated data loading function - optimized to load only active tab
const loadRestaurantData = async (city = null, reset = true) => {
  isLoading.value = true
  try {
    if (reset) {
      // Reset pagination when loading fresh data
      pagination.bookings.skip = 0
      pagination.preferred.skip = 0
      pagination.favorites.skip = 0
      pagination.friendsFavorites.skip = 0
      pagination.bookings.prefetchedData = null
      pagination.preferred.prefetchedData = null
      pagination.favorites.prefetchedData = null
      pagination.friendsFavorites.prefetchedData = null
      hasMoreData.value = true
      hasPrefetchedNext.value = false

      // Clear stale data so loading spinner shows instead of old results
      if (activeTab.value === 'preferred' || favoritesFilterSelection.value === 'Preferred') {
        restaurantsStore.preferredRestaurants = []
      } else if (favoritesFilterSelection.value === 'JustBooked') {
        restaurantsStore.bookedRestaurants = []
      } else if (favoritesFilterSelection.value === 'Friends') {
        restaurantsStore.friendsFavoriteRestaurants = []
      } else {
        restaurantsStore.favoritedRestaurants = []
      }
    }

    // OPTIMIZATION: Only load data for the active tab to reduce initial load time
    if (activeTab.value === 'preferred') {
      await restaurantsStore.getPreferredRestaurants(pagination.preferred.limit, city, pagination.preferred.skip, false)

      // Also preload city selector for preferred tab
      setTimeout(() => {
        if (!citySelectorData.value || !citySelectorData.value.cities) {
          console.log('Preloading city selector data in background...')
          restaurantsStore.getCitySelectorData()
        }
      }, 500)
    } else {
      // favorites tab - check if "Just Booked" filter is selected
      if (favoritesFilterSelection.value === 'JustBooked') {
        // Skip API call if user is not logged in or not platinum
        if (!isUserLoggedIn.value || !myUser.value?.isPlatinum) return

        await restaurantsStore.getBookedRestaurants(pagination.bookings.limit, false, city, pagination.bookings.skip, false, false, bookingsOccasionFilter.value || null)

        // Preload booked city selector data
        setTimeout(async () => {
          if (!bookedCitySelectorData.value?.cities || bookedCitySelectorData.value.cities.length <= 1) {
            console.log('Preloading booked city selector data in background...')
            bookedCitySelectorData.value = await restaurantsStore.getBookedCitySelectorData()
          }
        }, 500)
      } else if (favoritesFilterSelection.value === 'Preferred') {
        await restaurantsStore.getPreferredRestaurants(pagination.preferred.limit, city, pagination.preferred.skip, false, false, 'popular')
      } else if (favoritesFilterSelection.value === 'Friends') {
        await restaurantsStore.getFriendsFavoriteRestaurants(pagination.friendsFavorites.limit, city, pagination.friendsFavorites.skip)
      } else if (favoritesFilterSelection.value === 'MyStatus') {
        await Promise.all([
          preferredStatusStore.loadPreferredStatus(),
          preferredRestaurants.value.length === 0
            ? restaurantsStore.getPreferredRestaurants(200, null, 0, false)
            : Promise.resolve(),
        ])
      } else {
        // favorites tab - paginate like other tabs with server-side city filtering
        await restaurantsStore.getFavoritedRestaurants(pagination.favorites.limit, city, pagination.favorites.skip, false, false, favoritesSort.value)

        // Also preload favorites city selector for favorites tab
        setTimeout(async () => {
          if (!favoritedCitySelectorData.value?.cities || favoritedCitySelectorData.value.cities.length <= 1) {
            console.log('Preloading favorited city selector data in background...')
            favoritedCitySelectorData.value = await restaurantsStore.getFavoritedCitySelectorData()
          }
        }, 500)
      }
    }

    // Don't auto-prefetch on initial load - wait for user to scroll
    // This prevents unnecessary skip=20 calls when user might not scroll

    // Async fetch friends-favorited data (non-blocking, for all filters except Friends which has it inline)
    if (favoritesFilterSelection.value !== 'Friends') {
      fetchFriendsDataForRestaurants(displayedRestaurants.value)
    }
  } finally {
    isLoading.value = false
  }
}

// Prefetch next page of restaurants in background
const prefetchNextPage = async () => {
  // Don't prefetch if dialog is open (editingReservation is set)
  if (editingReservation?.value) {
    return
  }

  if (isPrefetching.value || !hasMoreData.value || hasPrefetchedNext.value) return

  isPrefetching.value = true
  try {
    const currentTab = activeTab.value

    // Map filter to pagination key
    let paginationKey = currentTab
    if (currentTab === 'favorites' && favoritesFilterSelection.value === 'JustBooked') paginationKey = 'bookings'
    else if (currentTab === 'favorites' && favoritesFilterSelection.value === 'Friends') paginationKey = 'friendsFavorites'
    else if (currentTab === 'favorites' && favoritesFilterSelection.value === 'Preferred') paginationKey = 'preferred'
    const currentPagination = pagination[paginationKey]

    // Calculate next skip position
    const nextSkip = currentPagination.skip + currentPagination.limit

    let prefetchedData
    if (currentTab === 'preferred') {
      prefetchedData = await restaurantsStore.getPreferredRestaurants(
        currentPagination.limit,
        selectedCity.value || null,
        nextSkip,
        false, // Don't append
        true // fetchOnly - don't update store, just return data
      )
    } else if (currentTab === 'favorites') {
      if (favoritesFilterSelection.value === 'JustBooked') {
        prefetchedData = await restaurantsStore.getBookedRestaurants(
          currentPagination.limit,
          false,
          selectedCity.value || null,
          nextSkip,
          false, // Don't append
          true, // fetchOnly - don't update store, just return data
          bookingsOccasionFilter.value || null
        )
      } else if (favoritesFilterSelection.value === 'Friends') {
        prefetchedData = await restaurantsStore.getFriendsFavoriteRestaurants(
          currentPagination.limit,
          selectedCity.value || null,
          nextSkip,
          false, // Don't append
          true // fetchOnly
        )
      } else if (favoritesFilterSelection.value === 'Preferred') {
        prefetchedData = await restaurantsStore.getPreferredRestaurants(
          currentPagination.limit,
          selectedCity.value || null,
          nextSkip,
          false, // Don't append
          true, // fetchOnly - don't update store, just return data
          'popular' // shared trending score
        )
      } else {
        prefetchedData = await restaurantsStore.getFavoritedRestaurants(
          currentPagination.limit,
          selectedCity.value || null,
          nextSkip,
          false, // Don't append
          true, // fetchOnly - don't update store, just return data
          favoritesSort.value // A-Z sort parameter
        )
      }
    }

    // If this is the final (partial) page, render it directly instead of caching.
    // At the bottom of the list the sentinel stops transitioning into view, so a
    // cached final page would never be flushed by loadMoreRestaurants — it would
    // be fetched but never shown, capping every Discover list at a multiple of the
    // page size (e.g. 20 of 28, 260 of 265). The store dedupes on append, so this
    // is safe even if loadMore also raced to fetch the same page.
    const isFinalPage = !prefetchedData || prefetchedData.length < currentPagination.limit
    if (isFinalPage) {
      if (prefetchedData && prefetchedData.length) {
        if (currentTab === 'preferred') {
          restaurantsStore.preferredRestaurants = dedupeById([...restaurantsStore.preferredRestaurants, ...prefetchedData])
        } else if (currentTab === 'favorites') {
          if (favoritesFilterSelection.value === 'JustBooked') {
            restaurantsStore.bookedRestaurants = dedupeById([...restaurantsStore.bookedRestaurants, ...prefetchedData])
          } else if (favoritesFilterSelection.value === 'Friends') {
            restaurantsStore.friendsFavoriteRestaurants = dedupeById([...restaurantsStore.friendsFavoriteRestaurants, ...prefetchedData])
          } else if (favoritesFilterSelection.value === 'Preferred') {
            restaurantsStore.preferredRestaurants = dedupeById([...restaurantsStore.preferredRestaurants, ...prefetchedData])
          } else {
            restaurantsStore.favoritedRestaurants = dedupeById([...restaurantsStore.favoritedRestaurants, ...prefetchedData])
          }
        }
        currentPagination.skip = nextSkip
        if (favoritesFilterSelection.value !== 'Friends') {
          fetchFriendsDataForRestaurants(prefetchedData)
        }
      }
      currentPagination.prefetchedData = null
      hasPrefetchedNext.value = false
      hasMoreData.value = false
    } else {
      // Full page — cache it for loadMoreRestaurants to flush on the next scroll.
      currentPagination.prefetchedData = prefetchedData
      hasPrefetchedNext.value = true
    }
  } catch (error) {
    console.error('Error prefetching:', error)
  } finally {
    isPrefetching.value = false
  }
}

// Load more restaurants for infinite scrolling
const loadMoreRestaurants = async () => {
  // Don't load more if dialog is open (editingReservation is set)
  if (editingReservation?.value) {
    return
  }

  if (isLoadingMore.value || !hasMoreData.value) return

  isLoadingMore.value = true
  try {
    const currentTab = activeTab.value

    // Map filter to pagination key
    let paginationKey = currentTab
    if (currentTab === 'favorites' && favoritesFilterSelection.value === 'JustBooked') paginationKey = 'bookings'
    else if (currentTab === 'favorites' && favoritesFilterSelection.value === 'Friends') paginationKey = 'friendsFavorites'
    else if (currentTab === 'favorites' && favoritesFilterSelection.value === 'Preferred') paginationKey = 'preferred'
    const currentPagination = pagination[paginationKey]

    // Check if we have prefetched data
    if (currentPagination.prefetchedData) {
      // Use cached prefetched data - instant!
      const cachedData = currentPagination.prefetchedData

      // Append the cached data (deduped — prefetch/load-more can race on a page)
      if (currentTab === 'preferred') {
        restaurantsStore.preferredRestaurants = dedupeById([...restaurantsStore.preferredRestaurants, ...cachedData])
      } else if (currentTab === 'favorites') {
        if (favoritesFilterSelection.value === 'JustBooked') {
          restaurantsStore.bookedRestaurants = dedupeById([...restaurantsStore.bookedRestaurants, ...cachedData])
        } else if (favoritesFilterSelection.value === 'Friends') {
          restaurantsStore.friendsFavoriteRestaurants = dedupeById([...restaurantsStore.friendsFavoriteRestaurants, ...cachedData])
        } else if (favoritesFilterSelection.value === 'Preferred') {
          restaurantsStore.preferredRestaurants = dedupeById([...restaurantsStore.preferredRestaurants, ...cachedData])
        } else {
          restaurantsStore.favoritedRestaurants = dedupeById([...restaurantsStore.favoritedRestaurants, ...cachedData])
        }
      }

      // Update skip position
      currentPagination.skip += currentPagination.limit

      // Clear cache
      currentPagination.prefetchedData = null
      hasPrefetchedNext.value = false

      // Fetch friends data for newly loaded restaurants (non-blocking)
      if (favoritesFilterSelection.value !== 'Friends') {
        fetchFriendsDataForRestaurants(cachedData)
      }

      // Immediately start prefetching next batch
      setTimeout(() => prefetchNextPage(), 100)
    } else {
      // Fallback: fetch if no prefetched data available
      currentPagination.skip += currentPagination.limit

      let newData
      if (currentTab === 'preferred') {
        newData = await restaurantsStore.getPreferredRestaurants(
          currentPagination.limit,
          selectedCity.value || null,
          currentPagination.skip,
          true // append to existing
        )
      } else if (currentTab === 'favorites') {
        if (favoritesFilterSelection.value === 'JustBooked') {
          newData = await restaurantsStore.getBookedRestaurants(
            currentPagination.limit,
            false,
            selectedCity.value || null,
            currentPagination.skip,
            true, // append to existing
            false,
            bookingsOccasionFilter.value || null
          )
        } else if (favoritesFilterSelection.value === 'Friends') {
          newData = await restaurantsStore.getFriendsFavoriteRestaurants(
            currentPagination.limit,
            selectedCity.value || null,
            currentPagination.skip,
            true // append to existing
          )
        } else if (favoritesFilterSelection.value === 'Preferred') {
          newData = await restaurantsStore.getPreferredRestaurants(
            currentPagination.limit,
            selectedCity.value || null,
            currentPagination.skip,
            true, // append to existing
            false, // fetchOnly
            'popular' // shared trending score
          )
        } else {
          newData = await restaurantsStore.getFavoritedRestaurants(
            currentPagination.limit,
            selectedCity.value || null,
            currentPagination.skip,
            true, // append to existing
            false, // fetchOnly
            favoritesSort.value // A-Z sort parameter
          )
        }
      }

      // If we got less data than limit, we've reached the end
      if (!newData || newData.length < currentPagination.limit) {
        hasMoreData.value = false
      }

      // Fetch friends data for newly loaded restaurants (non-blocking)
      if (favoritesFilterSelection.value !== 'Friends' && newData?.length) {
        fetchFriendsDataForRestaurants(newData)
      }

      // Start prefetching next batch
      setTimeout(() => prefetchNextPage(), 100)
    }
  } finally {
    isLoadingMore.value = false
  }
}

// Handle manual city selection changes
const handleCityChange = async () => {
  // Reload data with city filter for all tabs (server-side filtering)
  hasMoreData.value = true // Reset when changing city
  hasPrefetchedNext.value = false // Reset prefetch state
  await loadRestaurantData(selectedCity.value || null, true)
}

// Load city selector data if not already preloaded (fallback)
const handleCitySelectorFocus = async () => {
  // Load appropriate city data based on active tab and filter
  if (activeTab.value === 'favorites') {
    if (favoritesFilterSelection.value === 'JustBooked') {
      // The "Just Booked" filter has its own server-side city list
      if (!bookedCitySelectorData.value?.cities || bookedCitySelectorData.value.cities.length <= 1) {
        console.log('Booked city selector data not preloaded, loading now...')
        try {
          bookedCitySelectorData.value = await restaurantsStore.getBookedCitySelectorData()
        } catch (error) {
          console.error('Error loading booked city selector data:', error)
        }
      } else {
        console.log('Booked city selector data already loaded! ✅')
      }
    } else if (!favoritedCitySelectorData.value?.cities || favoritedCitySelectorData.value.cities.length <= 1) {
      // Other favorites filters share the favorited-city list
      console.log('Favorited city selector data not preloaded, loading now...')
      try {
        favoritedCitySelectorData.value = await restaurantsStore.getFavoritedCitySelectorData()
      } catch (error) {
        console.error('Error loading favorited city selector data:', error)
      }
    } else {
      console.log('Favorited city selector data already loaded! ✅')
    }
  } else {
    if (!citySelectorData.value || !citySelectorData.value.cities) {
      console.log('City selector data not preloaded, loading now...')
      try {
        await restaurantsStore.getCitySelectorData()
      } catch (error) {
        console.error('Error loading city selector data:', error)
      }
    } else {
      console.log('City selector data already loaded! ✅')
    }
  }
}

// Filtered cities for bottom sheet search
const filteredCityOptions = computed(() => {
  const cities = selectableCities.value
  if (!citySearchQuery.value) return cities
  const query = citySearchQuery.value.toLowerCase()
  
  return cities.filter(c => c.text.toLowerCase().includes(query))
})

// Open city filter bottom sheet
const openCityFilter = () => {
  pendingCitySelection.value = selectedCity.value
  citySearchQuery.value = ''
  isCityFilterOpen.value = true
  handleCitySelectorFocus()
}

// Apply city filter selection from bottom sheet
const applyCityFilter = () => {
  selectedCity.value = pendingCitySelection.value
  isCityFilterOpen.value = false

  if (favoritesFilterSelection.value !== 'Popular') {
    // Switch to the default Trending filter — the watcher will handle data reload with the new city
    favoritesFilterSelection.value = 'Popular'
  } else {
    // Already on Trending, just reload for the city change
    handleCityChange()
  }
}

// IntersectionObserver-based infinite scroll.
// Why: window 'scroll' events stop firing reliably on iOS Safari after a
// Vuetify overlay (e.g., the city-filter VBottomSheet) releases its scroll
// lock, which broke "load more" after picking a city.
const setupInfiniteScrollObservers = () => {
  if (!loadMoreSentinelEl.value) return

  if (loadMoreObserver) loadMoreObserver.disconnect()
  if (prefetchObserver) prefetchObserver.disconnect()

  prefetchObserver = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (
        entry.isIntersecting
        && !isLoading.value
        && hasMoreData.value
        && !hasPrefetchedNext.value
        && !isPrefetching.value
      ) {
        prefetchNextPage()
      }
    })
  }, { rootMargin: '1500px 0px' })

  loadMoreObserver = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (
        entry.isIntersecting
        && !isLoading.value
        && hasMoreData.value
        && !isLoadingMore.value
      ) {
        loadMoreRestaurants()
      }
    })
  }, { rootMargin: '600px 0px' })

  prefetchObserver.observe(loadMoreSentinelEl.value)
  loadMoreObserver.observe(loadMoreSentinelEl.value)
}

// Re-arm the infinite-scroll observers after every full reload (city change,
// filter change, tab change, occasion change, restaurant added — all of which
// toggle isLoading). The sentinel DOM node is stable, so on Chromium the
// original mount-time observer keeps working. iOS Safari/WebKit, however, does
// not reliably fire intersection changes caused by content-height changes
// (only by real scroll/paint), so after picking a non-default city the sentinel
// can move into view without ever firing "load more". Disconnecting and
// re-observing forces WebKit to take a fresh measurement and deliver an initial
// callback for the sentinel's current position. isLoadingMore/isPrefetching
// drive paging and do NOT toggle isLoading, so this only fires on full reloads.
watch(isLoading, async loading => {
  if (loading) return
  await nextTick()
  setupInfiniteScrollObservers()
})

// Watch for route query param changes (handles navigation back from checkout)
watch(() => route.query.tab, tabParam => {
  if (tabParam === 'instantBookings') {
    activeTab.value = 'instantBookings'

    // Clean up the URL using native History API (avoids triggering Vue Router reactivity)
    nextTick(() => {
      window.history.replaceState({}, '', route.path)
    })
  }
}, { immediate: true })

// Watch for tab changes and load data if needed
watch(activeTab, async (newTab, oldTab) => {
  hasMoreData.value = true
  hasPrefetchedNext.value = false

  // Save current city selection to the old tab's state
  if (oldTab === 'favorites') {
    favoritesSelectedCity.value = selectedCity.value
  } else if (oldTab === 'instantBookings') {
    instantBookingsSelectedCity.value = selectedCity.value
  }

  // Restore city selection from the new tab's state
  if (newTab === 'favorites') {
    selectedCity.value = favoritesSelectedCity.value
  } else if (newTab === 'instantBookings') {
    selectedCity.value = instantBookingsSelectedCity.value
  } else {
    selectedCity.value = DEFAULT_MARKET // preferred tab (if we add it back)
  }

  // OPTIMIZATION: Check if data is already preloaded
  // Always refetch instant bookings (time-sensitive, may be stale from admin page or other users)
  const hasData = newTab === 'preferred'
    ? preferredRestaurants.value && preferredRestaurants.value.length > 0
    : newTab === 'instantBookings'
      ? false // Always refetch instant bookings
      : favoritedRestaurants.value && favoritedRestaurants.value.length > 0

  if (!hasData) {
    // Load initial data - don't prefetch immediately on tab switch
    isLoading.value = true
    try {
      if (newTab === 'preferred') {
        console.log('Loading preferred restaurants (not preloaded)...')
        await restaurantsStore.getPreferredRestaurants(pagination.preferred.limit, null, 0, false)
      } else if (newTab === 'instantBookings') {
        console.log('Loading instant booking slots...')
        await instantBookingSlotsStore.fetchSlots({ status: 'available' })
      } else {
        console.log('Loading favorited restaurants with pagination...')
        await restaurantsStore.getFavoritedRestaurants(pagination.favorites.limit, favoritesSelectedCity.value || null, 0, false, false, favoritesSort.value)

        // Also load favorited city selector data
        if (!favoritedCitySelectorData.value?.cities || favoritedCitySelectorData.value.cities.length <= 1) {
          favoritedCitySelectorData.value = await restaurantsStore.getFavoritedCitySelectorData()
        }
      }
    } finally {
      isLoading.value = false
    }
  } else {
    console.log(`Tab switched to ${newTab} - data already loaded! ✅`)
  }

  // Don't auto-prefetch on tab switch - let scroll handler do it
})

// Instant bookings: Format date (e.g., "Sat, Feb 14")
const formatDate = (dateStr, timeZone) => {
  if (!dateStr) return ''
  const tz = timeZone || 'America/New_York'
  
  return moment.tz(dateStr, tz).format('ddd, MMM D')
}

// Instant bookings: Format time to show AM/PM (e.g., "7:30 PM")
const formatTime = timeStr => {
  if (!timeStr) return ''
  const parsed = moment(timeStr, ['HH:mm', 'H:mm'], true)
  if (!parsed.isValid()) {
    console.warn('Invalid time format:', timeStr)
    
    return timeStr
  }
  
  return parsed.format('h:mm A')
}

// Instant bookings: Deduplicate slots by date, time, and party size
const getUniqueSlots = slots => {
  const seen = new Set()

  return slots.filter(slot => {
    const key = `${slot.date}_${slot.time}_${slot.partySize}_${slot.experienceName || ''}`
    if (seen.has(key)) {
      return false
    }
    seen.add(key)

    return true
  })
}

// Maximum slots to display per restaurant before showing "view more"
const MAX_VISIBLE_SLOTS = 4

// Get limited unique slots for display (max 4)
const getLimitedSlots = slots => {
  return getUniqueSlots(slots).slice(0, MAX_VISIBLE_SLOTS)
}

// Check if restaurant has more slots than the display limit
const hasMoreSlots = slots => {
  return getUniqueSlots(slots).length > MAX_VISIBLE_SLOTS
}

// Get count of additional slots beyond the display limit
const getExtraSlotCount = slots => {
  return getUniqueSlots(slots).length - MAX_VISIBLE_SLOTS
}

// Instant bookings: Get restaurant image URL (delegates to shared helper)
const getRestaurantImageUrl = slot => getRestaurantImageUrlShared(slot?.restaurant)

// Instant bookings: Handle book slot - open appropriate dialog
const handleBookSlot = slot => {
  // Use seoId if available, otherwise fall back to _id for backward compatibility
  selectedSlotId.value = slot.seoId || slot._id

  if (slot.slotType === 'external') {
    showExternalBookingDialog.value = true
  } else {
    showCheckoutDialog.value = true
  }
}

// Handle successful booking from checkout dialog
const handleBookingSuccess = () => {
  // Dialog handles navigation to /bookings
}

// Instant bookings: Show restaurant details panel
const showRestaurantDetails = restaurant => {
  if (restaurant) {
    viewingDetailsRestaurantTab.value = "Details"
    viewingDetailsRestaurant.value = restaurant
  }
}

// Instant bookings: Clear filters
const clearInstantBookingFilters = () => {
  instantBookingsSelectedCity.value = ''
  instantBookingsSelectedDate.value = null
}

// Instant bookings: Handle city change
const handleInstantBookingCityChange = city => {
  instantBookingsSelectedCity.value = city
}

// Helper function to preload user profile image
const preloadProfileImage = profileImageId => {
  return new Promise(resolve => {
    if (!profileImageId) {
      resolve()
      
      return
    }
    const img = new Image()
    img.onload = () => resolve()
    img.onerror = () => resolve()
    img.src = mediaUrl(profileImageId, 'small')
    setTimeout(() => resolve(), 3000)
  })
}

// Handle add favorite click - check login status
const handleAddFavoriteClick = () => {
  // Check if user is logged in (not a dummy user and not null)
  if (!myUser.value || myUser.value.userLevel === 'dummy') {
    // User is not logged in, open request to join dialog
    loginDialogMode.value = 'activate'
  } else {
    // User is logged in, open add restaurant dialog
    isAddRestaurantDialogOpen.value = true
  }
}

// Open reservation dialog for a restaurant
const newDraft = async (market, restaurant) => {
  try {
    await usersStore.showLoadingOverlay()
    if (!myUser.value) {
      await usersStore.loginNewDummyUser()
    }
    await usersStore.startNewReservation({ restaurant, market })
    await nextTick()
    const currentUser = myUser.value
    if (currentUser?.profileImageId) {
      await preloadProfileImage(currentUser.profileImageId)
    }
  } catch (error) {
    console.error('Error creating new draft:', error)
  } finally {
    await usersStore.hideLoadingOverlay()
  }
}

// Instant booking note helpers
const isRestaurantFavorited = restaurant => {
  if (!myUser.value?._id) return false
  if (restaurant?.isFavorited) return true
  const favoritedBy = restaurant?.favoritedBy
  if (favoritedBy && Array.isArray(favoritedBy)) {
    return favoritedBy.some(id => id?.toString() === myUser.value._id?.toString())
  }
  
  return false
}

const getExistingNoteForRestaurant = restaurantId => {
  return restaurantId ? favoriteNotes.value[restaurantId] : null
}

const hasNoteForRestaurant = restaurantId => {
  return !!getExistingNoteForRestaurant(restaurantId)?.note
}

const isInstantBookingNoteDialogOpen = ref(false)
const instantBookingNoteText = ref('')
const instantBookingNoteSaving = ref(false)
const instantBookingNoteRestaurantId = ref(null)
const instantBookingNoteRestaurantName = ref('')

const openInstantBookingNoteDialog = restaurantGroup => {
  instantBookingNoteRestaurantId.value = restaurantGroup.restaurantId
  instantBookingNoteRestaurantName.value = restaurantGroup.restaurantName
  const existing = getExistingNoteForRestaurant(restaurantGroup.restaurantId)
  instantBookingNoteText.value = existing?.note || ''
  isInstantBookingNoteDialogOpen.value = true
}

const instantBookingNoteError = ref('')

const saveInstantBookingNote = async () => {
  instantBookingNoteSaving.value = true
  instantBookingNoteError.value = ''

  const res = await restaurantsStore.saveFavoriteNote(instantBookingNoteRestaurantId.value, instantBookingNoteText.value)

  instantBookingNoteSaving.value = false
  if (res && res.ok === false) {
    instantBookingNoteError.value = res.message

    return
  }
  isInstantBookingNoteDialogOpen.value = false
}

// Load restaurant data and persisted city selection
onMounted(async () => {
  isLoading.value = true
  try {
    // Default to Chicago on every app open (in-memory only; member can still pick other cities)
    await loadRestaurantData(DEFAULT_MARKET, true)

    await nextTick()
    setupInfiniteScrollObservers()
  } catch (error) {
    console.error('Error loading data:', error)
    isLoading.value = false
  }
})

// Watch for new "follow" notifications and silently refresh friends-favorited data
const notificationsLength = computed(() => myUser.value?.notifications?.length || 0)
watch(notificationsLength, (newLen, oldLen) => {
  if (newLen > oldLen) {
    const notifications = myUser.value?.notifications
    if (!notifications?.length) return
    const latest = notifications[notifications.length - 1]
    if (latest?.type === 'follow') {
      // Clear the dedup cache so all restaurant IDs are re-fetched with updated friends list
      restaurantsStore.clearFriendsFavoritedCache()
      fetchFriendsDataForRestaurants(displayedRestaurants.value)
    }
  }
})

// --- Viewport-based restaurant view tracking ---
const viewedRestaurantIds = new Set()
let viewBatchQueue = []
let viewBatchTimer = null

const flushViewBatch = () => {
  const batch = viewBatchQueue.splice(0)
  batch.forEach(id => restaurantsStore.recordProfileView(id))
  viewBatchTimer = null
}

const queueViewCount = restaurantId => {
  if (!restaurantId || viewedRestaurantIds.has(restaurantId)) return
  viewedRestaurantIds.add(restaurantId)
  viewBatchQueue.push(restaurantId)
  if (!viewBatchTimer) {
    viewBatchTimer = setTimeout(flushViewBatch, 1000)
  }
}

const viewObserver = new IntersectionObserver(entries => {
  entries.forEach(entry => {
    if (entry.isIntersecting) {
      const id = entry.target.dataset.restaurantViewId
      if (id) {
        queueViewCount(id)
        viewObserver.unobserve(entry.target)
      }
    }
  })
}, { threshold: 0.5 })

const onCardMounted = (el, restaurantId) => {
  if (el && restaurantId) {
    el.dataset.restaurantViewId = restaurantId
    viewObserver.observe(el)
  }
}

// Cleanup observers on unmount
onUnmounted(() => {
  if (loadMoreObserver) loadMoreObserver.disconnect()
  if (prefetchObserver) prefetchObserver.disconnect()
  viewObserver.disconnect()
  if (viewBatchTimer) {
    clearTimeout(viewBatchTimer)
    flushViewBatch()
  }
})
</script>

<template>
  <div>
    <AddRestaurantDialog
      v-model:is-dialog-visible="isAddRestaurantDialogOpen"
      @on-restaurant-added="() => loadRestaurantData(selectedCity.value || null, true)"
    />
    <div>
      <!-- Sticky Header + Filter Chips -->
      <VContainer class="discover-container discover-sticky-header">
        <!--
          Heading row: title left, city selector inline right (plain text
          + the same chevron shape as the card expand cue, green here) 
        -->
        <div class="mf-bar">
          <h2 class="discover-title">
            <CmsText k="discover.tabs.membersFavorites" />
          </h2>
          <span
            class="city-inline"
            role="button"
            tabindex="0"
            @click="openCityFilter"
            @keydown.enter="openCityFilter"
          >
            {{ selectableCities.find(c => c.value === selectedCity)?.text || selectedCity || 'All Cities' }}
            <svg
              class="city-chevron"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            ><path d="M6 9l6 6 6-6" /></svg>
          </span>
        </div>

        <!-- Horizontal Filter Chips -->
        <div class="filter-chips-bar">
          <VChip
            v-for="filter in favoritesFilterOptions"
            :key="filter.value"
            :color="favoritesFilterSelection === filter.value ? 'primary-dark' : undefined"
            :variant="favoritesFilterSelection === filter.value ? 'flat' : 'outlined'"
            class="filter-chip"
            :class="[{ 'filter-chip--active': favoritesFilterSelection === filter.value }]"
            size="small"
            @click="favoritesFilterSelection = filter.value"
          >
            <span>{{ filter.text }}</span>
          </VChip>
        </div>

        <!--
          Preferred tab descriptor — aligned to the heading column,
          tappable → the For Restaurants modal 
        -->
        <div
          v-if="favoritesFilterSelection === 'Preferred' && activeTab !== 'instantBookings'"
          class="preferred-descriptor"
          role="button"
          tabindex="0"
          @click="isShowingRestaurantsInfoDialog = true"
          @keydown.enter="isShowingRestaurantsInfoDialog = true"
        >
          <CmsText k="discover.preferred.descriptor" />
        </div>

        <!-- Filters for Instant Bookings tab -->
        <VRow
          v-if="activeTab === 'instantBookings'"
          no-gutters
          class="mb-1"
        >
          <VCol cols="6">
            <VAutocomplete
              v-model="instantBookingsSelectedCity"
              autocomplete="false"
              label="City"
              :items="[{ title: 'All Cities', value: '' }, ...availableInstantBookingCities.map(c => ({ title: getCityDisplayName(c), value: c }))]"
              :menu-props="{ density: 'compact', height: '100%', width: !smAndDown ? 'auto' : '50%', minHeight: '50%' }"
              item-title="title"
              item-value="value"
              no-data-text="No Matches"
              clear-on-select
              density="compact"
              @update:model-value="handleInstantBookingCityChange"
            />
          </VCol>
          <VCol cols="6">
            <div class="ml-1">
              <DatePickerDialog
                v-model="instantBookingsSelectedDate"
                :available-dates="availableInstantBookingDates"
                density="compact"
                @submit-request="newDraft('', '')"
              />
            </div>
          </VCol>
        </VRow>

        <!-- Icons legend removed - now shown as chip icons -->
        <div
          v-if="showInstantBookingsTab && activeTab === 'instantBookings'"
          class="favorites-explanation mb-4"
        >
          <p class="description mb-1 icon-text-row">
            <span
              class="text-content  text-center"
              style="font-size: 13px;"
            ><CmsText k="discover.labels.instantBookingsElevated" /></span>
          </p>
          <p class="description mb-0 indented-text">
            <!-- <strong><a href="/members">Platinum members</a></strong> can request favorites globally -->
          </p>
          <p class=" text-center align-center justify-center">
            <span
              class="d-inline-flex align-center cursor-pointer  text-center align-center justify-center"
              @click="newDraft('', '')"
            >
              <svg
                :width="smAndDown ? 16 : 20"
                :height="smAndDown ? 16 : 20"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="mr-1"
              >
                <ellipse
                  cx="8"
                  cy="8"
                  rx="8"
                  ry="8"
                  fill="var(--ds-brand-primary-dark)"
                />
                <path
                  d="M10.5261 7.99958H5.05243M7.78927 5.47327V10.5259"
                  stroke="white"
                  stroke-width="0.860438"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span class="description text-primary  text-center align-center justify-center"><CmsText k="discover.labels.submitRequestOther" /></span>
              
            </span>
          </p>
        </div>
      </VContainer>

      <section>
        <!-- Restaurant Grid (for favorites, preferred tabs) -->
        <VContainer
          v-if="activeTab !== 'instantBookings'"
          class="discover-container discover-grid-container"
        >
          <!-- Network tab context heading (logged-in only; prospects get the gate line) -->
          <h3
            v-if="favoritesFilterSelection === 'Friends' && isUserLoggedIn"
            class="network-context-heading"
          >
            <CmsText k="discover.network.contextHeading" />
          </h3>

          <!-- Network tab, logged out: one calm serif line, high under the tabs -->
          <div
            v-if="favoritesFilterSelection === 'Friends' && !isUserLoggedIn"
            class="gated-state"
          >
            <CmsText
              k="discover.gated.networkLine"
              tag="p"
              class="gate-line"
            />
          </div>

          <!-- Just Booked, logged out: gate line + one quiet stat (the only proof shown) -->
          <div
            v-else-if="favoritesFilterSelection === 'JustBooked' && !isUserLoggedIn"
            class="gated-state"
          >
            <CmsText
              k="discover.gated.justBookedLine"
              tag="p"
              class="gate-line"
            />
            <p class="gate-stat">
              <CmsText k="discover.gated.justBookedStatRestaurants" />
              <span class="gate-stat-sep">|</span>
              <CmsText k="discover.gated.justBookedStatCities" />
            </p>
          </div>

          <!-- Just Booked, logged in without Platinum: upgrade path (member-facing) -->
          <div
            v-else-if="favoritesFilterSelection === 'JustBooked' && !myUser?.isPlatinum"
            class="empty-state"
          >
            <p
              class="text-center text-grey"
              style="font-size: 14px;"
            >
              Viewable by Platinum members only
            </p>
            <div
              v-if="isUserLoggedIn"
              class="text-center mt-3"
            >
              <VBtn
                v-if="myUser?.pendingPlatinumApproval"
                color="primary"
                density="comfortable"
                disabled
              >
                Request Pending
              </VBtn>
              <VBtn
                v-else
                color="primary"
                density="comfortable"
                @click="requestUpgrade"
              >
                <VIcon
                  class="mr-2"
                  icon="mdi-arrow-up-circle"
                />
                Request Platinum
              </VBtn>
            </div>
          </div>

          <div
            v-else-if="hasDataForActiveTab"
            class="grid-wrapper"
          >
            <div class="restaurant-grid">
              <div
                v-for="(item, index) in displayedRestaurants"
                :key="item._id || item.raw?._id"
                :ref="(el) => onCardMounted(el, item.raw?._id || item._id)"
              >
                <RestaurantGridCard
                  :item="item"
                  :index="index"
                  :rank="activeTab === 'favorites' && favoritesFilterSelection === 'Popular' && index < 10 ? index + 1 : null"
                  :rank-gutter="activeTab === 'favorites' && favoritesFilterSelection === 'Popular'"
                  :hide-market="!!selectedCity"
                  :friends-who-favorited-prop="favoritesFilterSelection === 'Friends' ? (item.friendsWhoFavorited || []) : null"
                  :member-preferred="preferredStatusStore.isPreferredAt(item._id || item.raw?._id)"
                  @open-choose-member-dialog="(market, restaurant) => usersStore.startNewReservation({ restaurant, market })"
                />
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div
            v-else-if="isLoading"
            class="loading-state"
          >
            <VProgressCircular
              indeterminate
              color="primary"
              size="64"
            />
          </div>

          <!-- Empty State -->
          <div
            v-else
            class="empty-state"
          >
            <p
              class="text-center text-grey"
              style="font-size: 14px;"
            >
              <template v-if="favoritesFilterSelection === 'JustBooked'">
                <CmsText
                  k="discover.emptyState.noBookingsCity"
                  tag="div"
                />
                <CmsText
                  k="discover.emptyState.filterByAZ"
                  tag="div"
                />
              </template>
              <template v-else-if="favoritesFilterSelection === 'Friends'">
                {{ $t('discover.emptyState.noFriendsFavorites') }}
              </template>
              <template v-else-if="favoritesFilterSelection === 'MyStatus'">
                <CmsText
                  k="discover.status.emptyState"
                  tag="div"
                />
              </template>
              <template v-else-if="favoritesFilterSelection === 'Spaces'">
                <CmsText
                  k="discover.emptyState.noGroupSpaces"
                  tag="div"
                />
                <CmsText
                  k="discover.emptyState.filterByAZ"
                  tag="div"
                />
              </template>
              <template v-else-if="favoritesFilterSelection === 'Preferred'">
                <CmsText
                  k="discover.emptyState.noPreferredCity"
                  tag="div"
                />
                <CmsText
                  k="discover.emptyState.filterByAZ"
                  tag="div"
                />
              </template>
              <template v-else>
                {{ $t('discover.emptyState.noRestaurantsFound') }}{{ selectedCity ? ' for this city' : '' }}.
              </template>
            </p>
          </div>
        </VContainer>

        <!-- Instant Bookings Content -->
        <VContainer
          v-else
          class="discover-container discover-grid-container"
        >
          <!-- Loading State -->
          <div
            v-if="instantBookingSlotsLoading || isLoading"
            class="loading-state"
          >
            <VProgressCircular
              indeterminate
              color="primary"
              size="64"
            />
            <CmsText
              k="discover.emptyState.loadingSlots"
              tag="p"
              class="text-h6 text-medium-emphasis mt-4"
            />
          </div>

          <!-- Empty State -->
          <div
            v-else-if="filteredInstantBookingSlots.length === 0"
            class="empty-state"
          >
            <VIcon
              icon="mdi-calendar-remove"
              size="80"
              color="grey-lighten-1"
              class="mb-4"
            />
            <CmsText
              k="discover.emptyState.allTablesBooked"
              tag="h2"
              class="text-h5 mb-2"
            />
            <CmsText
              k="discover.emptyState.premiumTablesSecured"
              tag="p"
              class="text-body-1 text-medium-emphasis mb-4"
            />
          </div>

          <!-- Restaurant Slots List -->
          <div
            v-else
            class="restaurants-list"
          >
            <div
              v-for="restaurantGroup in slotsByRestaurant"
              :key="restaurantGroup.restaurantId"
              class="restaurant-row"
            >
              <div class="restaurant-header">
                <!-- Add Button (Left) -->
                <button
                  class="add-btn-left"
                  aria-label="Add to request"
                  @click.stop="newDraft(restaurantGroup.restaurantMarket, restaurantGroup.restaurantName)"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <ellipse
                      cx="8"
                      cy="8"
                      rx="8"
                      ry="8"
                      fill="var(--ds-brand-primary-dark)"
                    />
                    <path
                      d="M10.5261 7.99958H5.05243M7.78927 5.47327V10.5259"
                      stroke="white"
                      stroke-width="0.860438"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>

                <!-- Restaurant Info (Middle) -->
                <div class="restaurant-content">
                  <!-- Restaurant Name and Info -->
                  <div class="restaurant-info">
                    <div class="main-content-container">
                      <!-- Preferred Icon (always reserve space) -->
                      <div class="icon-placeholder">
                        <PreferredIcon
                          v-if="restaurantGroup.restaurant?.status === 'Preferred'"
                          :size="20"
                        />
                      </div>

                      <!-- Text Content -->
                      <div class="text-content-wrapper">
                        <div class="title-row">
                          <div class="title-with-heart">
                            <span
                              class="restaurant-name clickable"
                              :class="{ 'text-preferred': restaurantGroup.restaurant?.status === 'Preferred' }"
                              @click.stop="showRestaurantDetails(restaurantGroup.restaurant)"
                            >
                              {{ restaurantGroup.restaurantName }}
                            </span>
                            <button
                              class="heart-icon-btn"
                              aria-label="Favorite"
                              @click.stop
                            >
                              <RestaurantHeart
                                :restaurant-name="restaurantGroup.restaurantName"
                                :restaurant-market="restaurantGroup.restaurantMarket"
                                :restaurant="restaurantGroup.restaurant"
                              />
                            </button>
                          </div>
                        </div>

                        <div class="sub-content-container">
                          <p
                            v-if="restaurantGroup.restaurant?.group"
                            class="content-subtitle text-truncate"
                          >
                            {{ restaurantGroup.restaurant.group }}
                          </p>
                          <p class="content-subtitle text-truncate">
                            {{ restaurantGroup.restaurantMarket }}
                          </p>
                          <!-- Favorite Note -->
                          <div
                            v-if="myUser && isRestaurantFavorited(restaurantGroup.restaurant)"
                            class="favorite-note-text"
                            @click.stop="openInstantBookingNoteDialog(restaurantGroup)"
                          >
                            <VIcon
                              size="14"
                              :icon="hasNoteForRestaurant(restaurantGroup.restaurantId) ? 'mdi-note-text' : 'mdi-note-text-outline'"
                              :color="hasNoteForRestaurant(restaurantGroup.restaurantId) ? 'primary' : 'grey'"
                              class="cursor-pointer"
                              style="vertical-align: middle; margin-right: 2px;"
                            />
                            <span v-if="hasNoteForRestaurant(restaurantGroup.restaurantId)">{{ getExistingNoteForRestaurant(restaurantGroup.restaurantId).note }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Restaurant Image (Right) -->
                <div class="restaurant-image-container">
                  <VImg
                    v-if="getRestaurantImageUrl(restaurantGroup.slots[0])"
                    :src="getRestaurantImageUrl(restaurantGroup.slots[0])"
                    cover
                    class="restaurant-image"
                    aspect-ratio="1"
                  >
                    <template #placeholder>
                      <div class="d-flex align-center justify-center fill-height">
                        <VProgressCircular
                          indeterminate
                          color="grey-lighten-5"
                          size="32"
                        />
                      </div>
                    </template>
                  </VImg>
                  <!-- Fallback: Restaurant initial if no image -->
                  <div
                    v-else
                    class="restaurant-placeholder"
                  >
                    <span class="restaurant-initial">{{ restaurantGroup.restaurantName.charAt(0) }}</span>
                  </div>
                </div>
              </div>

              <!-- Available Slots (full width, aligned with plus icon) -->
              <div class="slots-container-wrapper">
                <div class="slots-container">
                  <div
                    v-for="slot in getLimitedSlots(restaurantGroup.slots)"
                    :key="slot._id"
                    class="slot-card"
                    @click="handleBookSlot(slot)"
                  >
                    <div class="slot-date">
                      {{ formatDate(slot.date, slot.restaurantTimeZone) }}
                    </div>
                    <div class="slot-time">
                      {{ formatTime(slot.time) }}
                    </div>
                    <div class="slot-details">
                      <span class="slot-party">
                        <VIcon
                          icon="mdi-account-outline"
                          size="14"
                        />
                        {{ slot.partySize }}
                      </span>
                    </div>
                  </div>
                </div>
                <!-- View More link when there are more than 4 slots - separate row -->
                <button
                  v-if="hasMoreSlots(restaurantGroup.slots)"
                  class="view-more-btn"
                  @click.stop="showRestaurantDetails(restaurantGroup.restaurant)"
                >
                  +{{ getExtraSlotCount(restaurantGroup.slots) }} more
                </button>
              </div>
            </div>
          </div>
        </VContainer>
        <div
          v-if="showInstantBookingsTab && activeTab === 'instantBookings'"
          class="text-center submit-request-cta"
          style="font-size: 13px; padding-top: 25px;line-height: 36px;"
        >
          <p>
            <span class="text-content"><CmsText k="discover.labels.moreTablesComing" /></span>
          </p>
        </div>
        <!-- Prefetching Indicator (outside container for full width) -->
        <VContainer class="discover-container">
          <!-- IntersectionObserver sentinel for iOS-safe infinite scroll -->
          <div
            ref="loadMoreSentinelEl"
            class="load-more-sentinel"
            aria-hidden="true"
          />
          <div
            v-if="isPrefetching && hasDataForActiveTab && !isLoading"
            class="prefetching-indicator"
          >
            <VProgressCircular
              indeterminate
              color="primary"
              size="24"
            />
            <CmsText
              k="discover.labels.loadingMore"
              tag="p"
              class="prefetching-text"
            />
          </div>

          <!-- End of Results Indicator -->
          <div
            v-else-if="!hasMoreData && hasDataForActiveTab && !isLoading"
            class="end-of-results"
          />
        </VContainer>
      </section>
    </div>
  </div>

  <UpgradeProfileDialog
    v-model="showUpgradeDialog"
    :product-id="upgradeProductId"
    :price-dollars="upgradePriceDollars"
    :message="upgradeMessage"
    @complete="onUpgradeDialogComplete"
    @cancel="onUpgradeDialogCancel"
  />

  <!-- Instant Booking Checkout Dialog -->
  <InstantBookingCheckoutDialog
    v-model="showCheckoutDialog"
    :slot-id="selectedSlotId"
    @success="handleBookingSuccess"
  />

  <!-- Instant Booking Note Editor Dialog -->
  <VDialog
    v-model="isInstantBookingNoteDialogOpen"
    max-width="420"
    @click:outside="isInstantBookingNoteDialogOpen = false"
  >
    <VCard class="note-dialog-card">
      <div class="note-dialog-header">
        <VIcon
          icon="mdi-note-text-outline"
          size="20"
          color="primary"
        />
        <span class="note-dialog-title">Store notes for {{ instantBookingNoteRestaurantName }}</span>
      </div>
      <VCardText class="note-dialog-body">
        <VTextarea
          v-model="instantBookingNoteText"
          placeholder="Favorite dishes, tables, team members, occasions, who recommended..."
          :counter="NOTE_MAX_LENGTH"
          :maxlength="NOTE_MAX_LENGTH"
          rows="3"
          auto-grow
          variant="outlined"
          density="compact"
          hide-details="auto"
          class="note-textarea"
        />
        <p
          v-if="instantBookingNoteError"
          class="text-error text-caption mt-1 mb-0"
        >
          {{ instantBookingNoteError }}
        </p>
      </VCardText>
      <VCardActions class="note-dialog-actions">
        <VSpacer />
        <VBtn
          variant="text"
          size="small"
          @click="isInstantBookingNoteDialogOpen = false"
        >
          Cancel
        </VBtn>
        <VBtn
          color="primary"
          variant="flat"
          size="small"
          rounded="lg"
          :loading="instantBookingNoteSaving"
          @click="saveInstantBookingNote"
        >
          Save Note
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>

  <!-- External Booking Dialog -->
  <ExternalBookingDialog
    v-model="showExternalBookingDialog"
    :slot-id="selectedSlotId"
  />

  <!-- City Filter Bottom Sheet -->
  <VBottomSheet v-model="isCityFilterOpen">
    <VCard class="city-filter-sheet">
      <div class="sheet-handle" />
      <div class="sheet-header">
        <span class="sheet-title">Filter by City</span>
        <VBtn
          icon
          variant="text"
          size="small"
          @click="isCityFilterOpen = false"
        >
          <VIcon size="20">
            mdi-close
          </VIcon>
        </VBtn>
      </div>
      <VDivider />

      <!-- Search -->
      <div class="city-search-wrapper">
        <VTextField
          v-model="citySearchQuery"
          placeholder="Search cities..."
          variant="outlined"
          density="compact"
          hide-details
          prepend-inner-icon="mdi-magnify"
          class="city-search-input"
        />
      </div>

      <!-- City list -->
      <div class="city-list">
        <div
          v-for="city in filteredCityOptions"
          :key="city.value"
          class="city-list-item"
          :class="{ 'city-list-item--boundary': !city.value }"
          @click="pendingCitySelection = city.value"
        >
          <span class="city-list-item-text">{{ city.text }}</span>
          <span
            class="city-radio"
            :class="{ 'city-radio--selected': pendingCitySelection === city.value }"
          />
        </div>
      </div>

      <!-- Done button -->
      <div class="city-done-wrapper">
        <VBtn
          block
          color="primary-dark"
          rounded="lg"
          size="large"
          @click="applyCityFilter"
        >
          Done
        </VBtn>
      </div>
    </VCard>
  </VBottomSheet>
</template>

<style lang="scss" scoped>
// Ensure consistent container padding and width at all breakpoints
.discover-container {
  padding-bottom: 0 !important;
  margin-left: auto !important;
  margin-right: auto !important;
  max-width: 100%;
  overflow-x: hidden;
}

/* Heading row: title left, city selector inline right */
.mf-bar {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--ds-space-4);
}

.city-inline {
  font-family: var(--ds-font-body);
  font-size: var(--ds-text-md); // 16px — a real, tappable control beside the heading
  font-weight: var(--ds-font-semibold);
  // Ink, not green — green is spent on signals only (heart/count,
  // Preferred word, links, active tab), per the prototype
  color: var(--ds-brand-ink);
  cursor: pointer;
  white-space: nowrap;
}

.city-chevron {
  width: 14px;
  height: 14px;
  vertical-align: -2px;
}

@media (min-width: 960px) {
  .city-inline {
    font-size: var(--ds-text-lg); // 18px against the 32px heading
  }

  .city-chevron {
    width: 16px;
    height: 16px;
  }
}

/* Preferred tab — one quiet descriptor line, left edge on the heading
   column; taps open the For Restaurants modal */
.preferred-descriptor {
  padding-top: var(--ds-space-3);
  font-family: var(--ds-font-body);
  font-size: var(--ds-text-xs);
  font-weight: var(--ds-font-medium);
  color: var(--ds-brand-primary-dark);
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
}

/* Shared content column: header + list cap at 720px content (752px incl.
   the 16px container gutters) and center, so the heading, tabs, descriptor,
   and card rows all share one left edge with the hero above */
.discover-grid-container {
  padding-top: 0 !important;
  margin-left: auto !important;
  margin-right: auto !important;
  max-width: 752px;
  overflow-x: hidden;
}

/* Desktop: the shared column widens to the two-column grid (2×560 + 24px
   gap = 1144 content, 1176 incl. gutters) — header + hero match */
@media (min-width: 961px) {
  .discover-grid-container {
    // Shared ~1160px container (matches the navbar contents)
    max-width: 1160px;
    padding-left: 32px !important;
    padding-right: 32px !important;
  }
}

// Sticky header with city selector
.discover-sticky-header {
  padding-top: 12px !important;
  padding-bottom: 4px !important;
  max-width: 752px;
  margin-inline: auto;
}

@media (min-width: 961px) {
  .discover-sticky-header {
    // Shared ~1160px container (matches the navbar contents)
    max-width: 1160px;
    padding-left: 32px !important;
    padding-right: 32px !important;
  }
}

.discover-title {
  font-family: var(--ds-font-heading);
  font-size: var(--ds-text-xl);
  font-weight: var(--ds-font-medium);
  color: var(--ds-brand-ink);
  text-align: left;
  margin-bottom: 0;
}

@media (min-width: 960px) {
  .discover-title {
    font-size: var(--ds-text-3xl); // prototype: 30px heading on desktop
  }
}

// Context heading shown when the Network tab is selected
.network-context-heading {
  font-family: var(--ds-font-heading);
  font-size: var(--ds-text-lg);
  font-weight: var(--ds-font-semibold);
  text-align: center;
  margin-bottom: var(--ds-space-3);
}

// City filter bottom sheet
.city-filter-sheet {
  border-radius: 16px 16px 0 0 !important;
  min-block-size: 70vh;
  max-block-size: 70vh;
  display: flex;
  flex-direction: column;
}

.sheet-handle {
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: rgba(var(--v-theme-on-surface), 0.2);
  margin: 10px auto 4px;
}

.sheet-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px 8px 20px;
}

.sheet-title {
  font-size: 16px;
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface), 0.87);
}

.city-search-wrapper {
  padding: 12px 16px 8px;
}

.city-search-input {
  :deep(.v-field) {
    border-radius: 10px;
    font-size: 16px; // prevents iOS zoom
  }
}

.city-list {
  overflow-y: auto;
  flex: 1;
  padding: 0 4px;
}

.city-list-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  cursor: pointer;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.06);

  // Boundary under the All Cities row: pinned most-requested markets above,
  // alphabetical list below
  &.city-list-item--boundary {
    border-bottom: 2px solid var(--ds-border-strong);
  }

  &:last-child {
    border-bottom: none;
  }

  &:active {
    background: rgba(var(--v-theme-on-surface), 0.04);
  }
}

.city-list-item-text {
  font-family: var(--ds-font-body);
  font-size: 15px;
  font-weight: 400;
  color: rgba(var(--v-theme-on-surface), 0.87);
}

.city-radio {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid rgba(var(--v-theme-on-surface), 0.3);
  flex-shrink: 0;
  transition: all 0.15s ease;

  &--selected {
    border-color: var(--ds-brand-primary-dark);
    background: var(--ds-brand-primary-dark);
    box-shadow: inset 0 0 0 3px #fff;
  }
}

.city-done-wrapper {
  padding: 12px 16px;
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

// Text-only filter chips — tight enough that all four fit one row on mobile
.filter-chips-bar {
  display: flex;
  flex-wrap: nowrap;
  justify-content: flex-start;
  gap: 6px;
  padding-top: var(--ds-space-4);
  padding-bottom: 12px;
  max-width: 100%;
  overflow-x: auto;
}

.filter-chip {
  flex-shrink: 0;
  font-family: var(--ds-font-body) !important;
  font-size: var(--ds-text-xs) !important;
  font-weight: 500 !important;
  letter-spacing: 0 !important;
  border-color: #d0d0d0 !important;
  border-radius: 20px !important;
  transition: all 0.2s ease !important;

  &:not(.filter-chip--active) {
    color: #666 !important;

    &:hover {
      border-color: var(--ds-brand-primary-dark) !important;
      color: var(--ds-brand-primary-dark) !important;
    }
  }

  &.filter-chip--active {
    font-weight: 600 !important;
    box-shadow: 0 1px 4px rgba(63, 153, 143, 0.25);
  }
}

// "Can't find your favorite?" card above grid
.add-restaurant-card {
  display: flex;
  align-items: center;
//  border: 2px dashed #d0d0d0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 16px;

  &:hover {
    border-color: var(--ds-brand-primary-dark);
    background: rgba(63, 153, 143, 0.04);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(63, 153, 143, 0.15);
  }

  &:active {
    transform: translateY(0);
  }
}

.add-restaurant-card-inner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  width: 100%;
}

.add-restaurant-card-title {
  font-family: var(--ds-font-body);
  font-size: 13px;
  font-weight: 500;
  color: var(--ds-text-muted);
}

.add-restaurant-card-action {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: var(--ds-font-body);
  font-size: 12px;
  font-weight: 600;
  color: var(--ds-brand-primary-dark);
}

// Hero banner styles
.instant-booking-hero {
  overflow: hidden;

  .hero-overlay {
    position: relative;
    height: 100%;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, transparent 70%);
  }
}

.grid-wrapper {
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

.restaurant-grid {
  display: grid;
  // Line-separated rows: a single column on mobile; two columns on
  // desktop (inside the centered container) so rows stay a comfortable
  // width and the page fills down instead of stretching one column wide
  grid-template-columns: minmax(0, 1fr);
  gap: 0;
  width: 100%;
  max-width: 100%;
}

@media (min-width: 961px) {
  .restaurant-grid {
    // Prototype: capped column measure — rows stay contained instead of
    // stretching to fill half the container
    grid-template-columns: repeat(2, minmax(0, 520px));
    justify-content: space-between;
    column-gap: 56px;
    row-gap: 0;
  }
}

/* The v-for wrapper divs are the real grid items — stretch the card to
   fill each row track so paired columns share a height and their
   hairlines align */
.restaurant-grid > div {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.restaurant-grid > div :deep(.card-grid-item) {
  flex: 1;
}

.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
  min-height: 200px;
}

.empty-state {
  padding: 40px 20px;
  text-align: center;
  color: var(--ds-text-muted);
}

/* Logged-out gate on Just Booked / Network — understated serif line sitting
   high under the tab row (not vertically centered), generous whitespace,
   no lock icon, no CTA, no feed */
.gated-state {
  padding: var(--ds-space-8) var(--ds-space-6) var(--ds-space-16);
  text-align: center;

  /* Generous whitespace below the gate line so the page scrolls out
     gently instead of cutting off right at the text */
  min-height: 45vh;
}

.gate-line {
  font-family: var(--ds-font-heading);
  font-weight: var(--ds-font-medium);
  font-size: var(--ds-text-lg);
  line-height: var(--ds-leading-snug);
  color: var(--ds-brand-ink);

  /* "Viewable by members only" (24ch) stays one line; the longer Network
     line wraps to two balanced lines with no orphaned last word */
  max-width: 26ch;
  margin: 0 auto;
  text-wrap: balance;
}

.gate-stat {
  margin-top: var(--ds-space-3);
  font-size: var(--ds-text-xs);
  letter-spacing: var(--ds-tracking-wide);
  color: var(--ds-text-muted);
}

.gate-stat-sep {
  color: var(--ds-border-strong);
  margin: 0 var(--ds-space-2);
}

.prefetching-indicator {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 20px;
  gap: 12px;
  opacity: 0.6;
}

.prefetching-text {
  font-size: 12px;
  font-weight: 400;
  color: var(--ds-text-muted);
  margin: 0;
}

.end-of-results {
  padding: 30px 20px;
  text-align: center;
}

.load-more-sentinel {
  width: 100%;
  height: 1px;
  pointer-events: none;
}

.description {
  font-family: var(--ds-font-body);
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%;
  color: var(--ds-text-muted);
}

.icon-text-row {
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-start;
  gap: 4px;
}

.icon-wrapper {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.text-content {
  flex: 1;
  min-width: 0;
}

.aligned-icon {
  flex-shrink: 0;
  margin-top: 0;
}

.indented-text {
  margin-left: 24px;
}

// Legacy add-favorite-cta styles removed - replaced by .add-restaurant-card in grid

// City dropdown font size
:deep(.v-field__input) {
  font-size: 16px !important;
}

:deep(.v-autocomplete .v-autocomplete__selection-text) {
  overflow: unset !important;
}

// (Stale 961–1279px 16px-padding override removed — the shared 1160px
// container keeps 32px side padding at every desktop width, matching the
// navbar, so the list never pokes past the nav's content edges.)


// Instant bookings styles
.restaurants-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

.restaurant-row {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  background: white;
  border: 1px solid #E0E0E0;
  border-radius: 12px;
  transition: all 0.2s ease;
  overflow: hidden;
  max-width: 100%;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border-color: var(--ds-brand-primary-dark);
  }
}

.restaurant-header {
  display: flex;
  align-items: stretch;
  gap: 0px;
  min-width: 0;
  overflow: hidden;
}

.restaurant-image-container {
  flex-shrink: 0;
  width: 86px;
  height: 86px;
  border-radius: 8px;
  overflow: hidden;
  align-self: flex-start;
}

.restaurant-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.restaurant-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgb(var(--v-theme-primary-dark)) 0%, rgb(var(--v-theme-primary)) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.restaurant-initial {
  font-family: var(--ds-font-body);
  font-size: 40px;
  font-weight: 700;
  color: white;
  text-transform: uppercase;
}

.restaurant-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 0.5rem;
}

.restaurant-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.main-content-container {
  display: flex;
  align-items: flex-start;
  gap: 0px;
}

.icon-placeholder {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  margin-top: 2px;
  min-width: 20px;
  width: 20px;
}

.text-content-wrapper {
  flex: 1;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

.title-row {
  margin-bottom: 4px;
  min-width: 0;
  overflow: hidden;
}

.title-with-heart {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  overflow: hidden;
}

.restaurant-name {
  font-family: var(--ds-font-body);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  color: #050000;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &.clickable {
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover {
      color: var(--ds-brand-primary-dark);
      text-decoration: underline;
    }
  }
}

.text-preferred {
  color: var(--ds-brand-primary-dark);

  &.clickable:hover {
    color: #2d756d;
  }
}

.heart-icon-btn {
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ds-text-muted);
  transition: color 0.2s ease;
  flex-shrink: 0;

  &:hover {
    color: #E91E63;
  }
}

.add-btn-left {
  flex-shrink: 0;
  background: transparent;
  border: none;
  padding: 4px;
  gap: 0px;
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
}

.sub-content-container {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

.content-subtitle {
  font-family: var(--ds-font-body);
  font-size: 12px;
  font-weight: 400;
  line-height: 1.2;
  color: #585858;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.slots-container-wrapper {
  width: 100%;
  max-width: 100%;
  padding-left: 0;
  overflow: hidden;
}

.slots-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  max-width: 100%;
}

.slot-card {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.75rem 1rem;
  background: white;
  border: 1px solid #E0E0E0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 90px;

  &:hover {
    background: #f5f5f5;
    border-color: var(--ds-brand-primary-dark);
    transform: translateY(-1px);
  }
}

.slot-date {
  font-family: var(--ds-font-body);
  font-size: 11px;
  font-weight: 400;
  color: #585858;
}

.slot-time {
  font-family: var(--ds-font-body);
  font-size: 15px;
  font-weight: 600;
  color: #050000;
}

.slot-details {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: var(--ds-font-body);
  font-size: 13px;
  color: #585858;
}

.slot-party {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.view-more-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1rem;
  min-width: 90px;
  margin-top: 0.75rem;
  background: transparent;
  border: 1px dashed var(--ds-brand-primary-dark);
  border-radius: 8px;
  color: var(--ds-brand-primary-dark);
  font-family: var(--ds-font-body);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(63, 153, 143, 0.1);
    border-style: solid;
  }
}

// Favorite note in instant booking cards
.favorite-note-text {
  font-family: var(--ds-font-body);
  font-size: 11px;
  color: rgba(var(--v-theme-on-surface), 0.6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  line-height: 1.3;
  padding-top: 5px;
}

.note-dialog-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.note-dialog-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 20px 24px 8px;
}

.note-dialog-title {
  font-size: 16px;
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface), 0.87);
}

.note-dialog-body {
  padding: 12px 24px 8px !important;
}

.note-textarea :deep(.v-field) {
  border-radius: 12px !important;
}

.note-dialog-actions {
  padding: 8px 20px 16px !important;
}

// Instant bookings mobile responsiveness
@media (max-width: 960px) {
  .restaurants-list {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .restaurant-row {
    padding: 8px;
    gap: 8px;
    min-height: 100px;
  }

  .restaurant-image-container {
    width: 70px;
    height: 70px;
  }

  .restaurant-name {
    font-size: 13px;
  }

  .content-subtitle {
    font-size: 11px;
  }

  .slots-container {
    gap: 0.5rem;
  }

  .add-btn-left svg {
    width: 20px;
    height: 20px;
  }
}

@media (max-width: 600px) {
  .restaurants-list {
    gap: 10px;
  }

  .slot-card {
    min-width: 70px;
    padding: 0.5rem 0.625rem;
  }

  .slot-date {
    font-size: 10px;
  }

  .slot-time {
    font-size: 13px;
  }

  .slot-details {
    font-size: 11px;
  }

  .view-more-btn {
    min-width: 70px;
    padding: 0.5rem 0.625rem;
    font-size: 11px;
    margin-top: 0.5rem;
  }
}

@media (min-width: 961px) {
  .restaurant-row {
    padding: 16px;
    gap: 16px;
    min-height: 120px;
  }

  .restaurant-name {
    font-size: 16px;
  }

  .content-subtitle {
    font-size: 13px;
  }

  .restaurant-image-container {
    width: 88px;
    height: 88px;
  }
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: Auth
</route>
